<script setup lang="ts">
import '@banno/jha-wc/src/lists/jha-list/jha-list.js'
import BltModalDialog from '@/common/components/bltDialog/bltModalDialog.vue'
import type IWorkspace from '@/common/models/IWorkspace'
import dayjs from 'dayjs'
import {$inj} from '@/common/decorators/depinject'
import WorkspaceHistoryService, {
  type ApplicantInfo,
  type WorkspaceHistory
} from '@/common/services/WorkspaceHistory/workspaceHistoryService'
import {computed, onMounted, ref} from 'vue'
import HistoryDialogListItem from '@/branchmanager/app/components/workspaceHistoryDialog/historyDialogListItem.vue'
import BltEmptyData from '@/common/components/bltEmptyData/bltEmptyData.vue'
import WorkflowFactory, {type Workflow} from '@/common/services/Workflow/WorkFlowFactory'
import StartWorkflowFactory from '@/common/services/Workflow/StartWorkflowFactory'
import type StartWorkflow from '@/common/data/StartWorkflow'
import "@banno/jha-wc/src/icons/jha-icon-receipt.js";
import "@banno/jha-wc/src/lists/jha-list-item/jha-list-item.js";

const props = defineProps<{
  currentWorkspaceUUID: string
  currentWorkspace: IWorkspace
  showDialog: boolean
  history: Array<WorkspaceHistory>
}>()

const emits = defineEmits<{
  (e: 'closeModal'): void
}>()

const workspaceHistoryService = $inj(WorkspaceHistoryService)
const workflowFactory = $inj(WorkflowFactory)
const startWorkflowFactory = $inj(StartWorkflowFactory)

const workflowMap = ref<Map<string, Workflow>>(new Map())
const startWorkflowMap = ref<Map<string, StartWorkflow>>(new Map())
const applicantInfo = ref<Map<number, ApplicantInfo>>(new Map())
const loading = ref(true)

onMounted(() => {
  return Promise.all([getApplicantInfo(), getWorkflows()])
    .then(([applicantInfoRes]) => {
      getStartWorkflows()
      applicantInfo.value = applicantInfoRes
    })
    .finally(() => (loading.value = false))
})

const getApplicantInfo = () => {
  const map = new Map<number, ApplicantInfo>()
  return Promise.all(
    applicantIds.value.map((applicantId) => {
      return workspaceHistoryService.getApplicantInfo(props.currentWorkspaceUUID, applicantId).then((response) => {
        map.set(applicantId, response)
      })
    })
  ).then(() => {
    return map
  })
}

const applicantIds = computed(() => {
  const result = new Set<number>()

  props.history.forEach((item) => {
    if (item.state?.workflow && item.state?.activeApplicantId) {
      result.add(item.state?.activeApplicantId)
    }
  })

  return Array.from(result)
})

const workflows = computed(() => {
  const result = new Set<string>()

  props.history.forEach((item) => {
    if (item.state?.workflow) {
      result.add(item.state.workflow)
    }
  })

  return Array.from(result)
})

const startWorkflows = computed(() => {
  const result = new Set<string>()

  for (const [key, value] of workflowMap.value) {
    result.add(value.startWorkflowCode)
  }

  return Array.from(result)
})

const getWorkflows = () => {
  return workflows.value.map((workflow) => {
    return workflowMap.value.has(workflow)
      ? workflowMap.value.get(workflow)
      : fetchWorkflow(workflow)
  })
}

const getStartWorkflows = () => {
  return startWorkflows.value.map((startWorkflowCode) => {
    return startWorkflowMap.value.has(startWorkflowCode)
      ? startWorkflowMap.value.get(startWorkflowCode)
      : fetchStartWorkflow(startWorkflowCode)
  })
}

const fetchWorkflow = (workflowName: string) => {
  return workflowFactory.getWorkflow(workflowName).then((response) => {
    workflowMap.value.set(workflowName, response)
  })
}

const fetchStartWorkflow = (startWorkflowName: string) => {
  return startWorkflowFactory.get(startWorkflowName).then((response) => {
    startWorkflowMap.value.set(startWorkflowName, response)
  })
}

const trackers = computed(() => {
  const result = new Set<string>()

  props.history.forEach((item) => {
    result.add(item.trackerUuid)
  })

  return Array.from(result).join(', ')
})
</script>

<template>
  <blt-modal-dialog
    :showDialog="showDialog"
    title="Applicant state history"
    :showFooter="false"
    @cancel="emits('closeModal')"
    type="large-dialog-centered">
    <template #dialog-content>
      <jha-progress
        card
        v-if="loading" />
      <div
        v-else
        style="width: 100%">
        <dl>
          <dt>Workspace Type</dt>
          <dd>{{ currentWorkspace.workspaceType }}</dd>

          <dt v-if="workflows">Start Workflow(s)</dt>
          <dd>
            <ul>
              <li v-for="startWorkflow in startWorkflows">
                {{startWorkflowMap.get(startWorkflow)?.name}}
              </li>
            </ul>
          </dd>

          <dt v-if="workflows">Workflow(s)</dt>
          <dd>
            <ul>
              <li v-for="workflow in workflows">
                {{workflowMap.get(workflow)?.displayName}}<br/><i>({{workflow}})</i>
              </li>
            </ul>
          </dd>

          <dt v-if="trackers">Tracker</dt>
          <dd>{{ trackers }}</dd>

          <dt>Started</dt>
          <dd>{{ dayjs(currentWorkspace.createdDateIso).format('M/D/YY @ hh:mma') }}</dd>
        </dl>

        <blt-empty-data v-if="!history.length"
                        :blt-text="`No Applicant Channel history for this workspace`">
          <template #jhaIcon>
            <jha-icon-receipt class="secondary-fill-color"></jha-icon-receipt>
          </template>
        </blt-empty-data>

        <jha-list>
          <jha-list-item
            v-for="(historyItem, index) in history"
            :key="historyItem.createdTimestamp">
            <history-dialog-list-item
              :historyItem
              :nextHistory="index < history.length ? history[index + 1] : undefined"
              :applicantInfo />
          </jha-list-item>
        </jha-list>
      </div>
    </template>
  </blt-modal-dialog>
</template>

<style scoped>
jha-list-item {
  --jha-list-item-margin-left: 0;
  --jha-list-item-vertical-spacing: 0;
  --jha-list-item-horizontal-spacing: 0;
}

dt {
  font: var(--jh-font-weight-500) var(--jh-font-size-300) / var(--jh-font-line-height-400) var(--jh-font-family-primary);
}

dd {
  margin: 0 0 var(--jh-space-200) 0;
  color: var(--jha-text-dark);
  font: var(--jh-font-weight-400) var(--jh-font-size-350) / var(--jh-font-line-height-500) var(--jh-font-family-primary);
}

ul {
  list-style: none;
  margin: 0;
  padding:0
}
</style>
